// PRD.304 — Login "Forgot password?" recovery.
//
// Minimal, feature-specific logic for the login card's recovery state,
// extracted from LoginPage so the validation, concurrency guard, generic
// confirmation copy, and the two state transitions are unit testable under the
// repo's node:test harness (no DOM, no live Firebase).
//
// Deliberately NOT a general auth state machine — it covers exactly the
// Sign In ⇄ Recovery ⇄ Confirmation moves this feature needs.

/** Which state the login card is showing. */
export type LoginMode = 'signin' | 'recovery' | 'sent';

/** The login form fields that survive (or must not survive) a transition. */
export type LoginFormFields = { email: string; password: string };

/** Mutable in-flight gate, shared across clicks to block concurrent requests. */
export type LoginResetGate = { inFlight: boolean };

export type LoginResetDeps = {
  gate: LoginResetGate;
  /** Sends the reset email. Injected so tests never touch Firebase. */
  send: (email: string) => Promise<void>;
};

export type LoginResetResult =
  | { status: 'sent'; email: string; message: string }
  | { status: 'busy' }
  | { status: 'error'; message: string };

// Same email convention already used elsewhere in web-admin (ClinicSettingsPage,
// QuickPatientDrawer). Intentionally permissive — Firebase is the real authority.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EMPTY_EMAIL_MESSAGE = 'Please enter your email address.';
export const INVALID_EMAIL_MESSAGE = 'Please enter a valid email address.';

/** Generic fallback for anything that is not already a user-safe Error. */
export const GENERIC_ERROR_MESSAGE = 'Could not send the password reset email. Please try again.';

/**
 * Non-enumerating confirmation. Worded identically whether or not an account
 * exists — the caller must never branch on account existence when building it.
 */
export function genericResetConfirmation(email: string): string {
  return `If an account exists for ${email}, we've sent a password reset link. Check your inbox and spam folder.`;
}

/**
 * Open the recovery state from Sign In.
 *
 * Keeps any email already typed, and clears the password at the moment of
 * entry so it can never be carried into — or restored out of — recovery.
 */
export function enterRecovery(fields: LoginFormFields): LoginFormFields & { mode: LoginMode } {
  return { mode: 'recovery', email: fields.email, password: '' };
}

/** Return to Sign In from recovery or confirmation, keeping the email only. */
export function backToSignIn(fields: LoginFormFields): LoginFormFields & { mode: LoginMode } {
  return { mode: 'signin', email: fields.email, password: '' };
}

/**
 * Request a password reset for a typed email address.
 *
 * Returns `busy` (without sending) while a previous request is in flight, so
 * repeated clicks cannot produce concurrent Firebase calls. A successful
 * request always yields the same generic confirmation.
 */
export async function requestLoginPasswordReset(
  deps: LoginResetDeps,
  rawEmail: string,
): Promise<LoginResetResult> {
  if (deps.gate.inFlight) return { status: 'busy' };

  const email = rawEmail.trim();
  if (!email) return { status: 'error', message: EMPTY_EMAIL_MESSAGE };
  if (!EMAIL_RE.test(email)) return { status: 'error', message: INVALID_EMAIL_MESSAGE };

  deps.gate.inFlight = true;
  try {
    await deps.send(email);
    return { status: 'sent', email, message: genericResetConfirmation(email) };
  } catch (error: unknown) {
    // Only an Error's own message is trusted — `requestPasswordResetForEmail`
    // guarantees those are already mapped to user-safe copy. Anything else
    // (a raw FirebaseError-shaped object, a string) falls back to the generic
    // message so codes and stacks never reach the UI.
    const message = error instanceof Error && error.message ? error.message : GENERIC_ERROR_MESSAGE;
    return { status: 'error', message };
  } finally {
    deps.gate.inFlight = false;
  }
}
